import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Check, X, ChevronRight, CreditCard, Percent, Trash2 } from "lucide-react";
import { api } from "@/api/client";
import { useManageBusiness } from "@/pages/manage/ManageShell";
import { StatusBadge } from "@/components/StatusTimeline";
import { money } from "@/lib/pricing";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";

const NEXT = { accepted: "baking", baking: "ready", ready: "completed" };
const FILTERS = ["all", "pending", "accepted", "baking", "ready", "completed", "rejected"];

export default function Orders() {
  const { businessId } = useManageBusiness();
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [depInput, setDepInput] = useState("");
  const [quoteInputs, setQuoteInputs] = useState({});
  const [totalInput, setTotalInput] = useState("");
  const [confirm, setConfirm] = useState(null); // { type: "delete"|"clear", order? }
  const [working, setWorking] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    api.listOrders(filter, businessId).then(setOrders).catch(() => {}).finally(() => setLoading(false));
  }, [filter, businessId]);

  useEffect(() => { reload(); }, [reload]);

  const refreshSelected = async (id) => {
    const o = await api.getOrder(id);
    setSelected(o);
    reload();
  };

  const setStatus = async (o, status) => {
    try {
      await api.updateOrderStatus(o.id, status);
      toast.success(`Order ${o.order_number} → ${status}`);
      await refreshSelected(o.id);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const applyDeposit = async (o) => {
    const pct = parseFloat(depInput);
    if (isNaN(pct) || pct < 0 || pct > 100) { toast.error("Enter a percent 0–100"); return; }
    try {
      await api.overrideDeposit(o.id, { deposit_percent: pct });
      toast.success("Deposit updated");
      setDepInput("");
      await refreshSelected(o.id);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const markPaid = async (o) => {
    try {
      await api.markBalancePaid(o.id, "cash");
      toast.success("Balance marked paid");
      await refreshSelected(o.id);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const sendQuote = async (o) => {
    const items = o.items
      .map((it, index) => ({ index, it }))
      .filter(({ it }) => it.is_quote)
      .map(({ index }) => ({ index, unit_price: parseFloat(quoteInputs[index]) }));
    if (items.some((i) => isNaN(i.unit_price) || i.unit_price < 0)) {
      toast.error("Enter a valid price for each custom item");
      return;
    }
    try {
      await api.setQuotePrice(o.id, items);
      toast.success("Quote sent — customer can now pay the deposit");
      setQuoteInputs({});
      await refreshSelected(o.id);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const sendOrderTotal = async (o) => {
    const total = parseFloat(totalInput);
    if (isNaN(total) || total < 0) { toast.error("Enter a valid total price"); return; }
    try {
      await api.setOrderTotal(o.id, total);
      toast.success("Total sent — customer can now pay the deposit");
      setTotalInput("");
      await refreshSelected(o.id);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const runConfirm = async () => {
    if (!confirm) return;
    setWorking(true);
    try {
      if (confirm.type === "delete") {
        await api.deleteOrder(confirm.order.id);
        toast.success(`Order ${confirm.order.order_number} deleted`);
        setSelected(null);
      } else if (confirm.type === "clear") {
        const res = await api.clearOrders(businessId);
        toast.success(`Cleared ${res.deleted} order${res.deleted === 1 ? "" : "s"}`);
        setSelected(null);
      }
      setConfirm(null);
      reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setWorking(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-display text-3xl" style={{ color: "var(--ink)" }}>Orders</h1>
        {orders.length > 0 && (
          <button
            onClick={() => setConfirm({ type: "clear" })}
            data-testid="clear-all-orders-btn"
            className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-semibold"
            style={{ color: "#b91c1c", border: "1px solid #fca5a5", background: "var(--surface)" }}
          >
            <Trash2 size={15} /> Clear all
          </button>
        )}
      </div>

      <div className="mt-5 flex gap-2 overflow-x-auto pb-2">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`order-filter-${f}`}
            className="shrink-0 px-3.5 py-1.5 rounded-full text-sm font-semibold capitalize transition"
            style={filter === f ? { background: "var(--brand)", color: "var(--brand-fg)" } : { background: "var(--surface)", color: "var(--muted)", border: "1px solid var(--border)" }}
          >
            {f}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mt-6 space-y-2">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-16 rounded-xl animate-pulse" style={{ background: "var(--surface-2)" }} />)}</div>
      ) : orders.length === 0 ? (
        <p className="mt-8 text-sm" style={{ color: "var(--muted)" }}>No orders in this view.</p>
      ) : (
        <div className="mt-6 space-y-2">
          {orders.map((o) => (
            <button
              key={o.id}
              onClick={() => { setSelected(o); setDepInput(""); setTotalInput(""); }}
              data-testid={`admin-order-${o.id}`}
              className="w-full flex items-center justify-between gap-4 rounded-xl p-4 text-left transition hover:bg-[var(--surface-2)]"
              style={{ background: "var(--surface)", border: "1px solid var(--border)" }}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold" style={{ color: "var(--ink)" }}>{o.order_number}</span>
                  <StatusBadge status={o.status} />
                  {o.balance_paid ? (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: "#d9f6e4", color: "#12633a" }}>PAID</span>
                  ) : o.deposit_paid ? (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: "var(--accent-soft)", color: "#8a6100" }}>DEPOSIT</span>
                  ) : null}
                </div>
                <div className="text-xs mt-0.5 truncate" style={{ color: "var(--muted)" }}>
                  {o.customer_name} · {o.customer_phone} · {o.pickup_date}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="font-display text-lg" style={{ color: "var(--brand)" }}>{money(o.total)}</span>
                <ChevronRight size={18} style={{ color: "var(--muted)" }} />
              </div>
            </button>
          ))}
        </div>
      )}

      <Sheet open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <SheetContent className="overflow-y-auto w-full sm:max-w-md">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle className="font-display text-2xl flex items-center gap-2">
                  {selected.order_number} <StatusBadge status={selected.status} />
                </SheetTitle>
                <SheetDescription>{selected.customer_name} · {selected.customer_phone}</SheetDescription>
              </SheetHeader>

              <div className="mt-5 space-y-4">
                <div className="rounded-xl p-3 text-sm" style={{ background: "var(--surface-2)" }}>
                  <div style={{ color: "var(--ink)" }}><span style={{ color: "var(--muted)" }}>When:</span> {selected.pickup_date}</div>
                  <div className="capitalize" style={{ color: "var(--ink)" }}><span style={{ color: "var(--muted)" }}>Type:</span> {selected.fulfillment_type?.replace("_", " ")}</div>
                  {selected.delivery_address && <div style={{ color: "var(--ink)" }}><span style={{ color: "var(--muted)" }}>Address:</span> {selected.delivery_address}</div>}
                  {selected.notes && <div style={{ color: "var(--ink)" }}><span style={{ color: "var(--muted)" }}>Notes:</span> {selected.notes}</div>}
                </div>

                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: "var(--muted)" }}>Items</div>
                  <div className="space-y-2">
                    {selected.items.map((it, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <div>
                          <div style={{ color: "var(--ink)" }}>{it.quantity}× {it.product_name}</div>
                          <div className="text-xs" style={{ color: "var(--muted)" }}>{[it.size, it.flavor].filter(Boolean).join(" · ")}{it.message_on_cake ? ` · "${it.message_on_cake}"` : ""}</div>
                        </div>
                        <div style={{ color: "var(--ink)" }}>{selected.is_large_order ? <span style={{ color: "var(--muted)" }}>—</span> : it.is_quote ? "Quote" : money(it.subtotal)}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-xl p-3 space-y-1.5 text-sm" style={{ background: "var(--surface-2)" }}>
                  <div className="flex justify-between" style={{ color: "var(--muted)" }}><span>Total</span><span style={{ color: "var(--ink)", fontWeight: 600 }}>{money(selected.total)}</span></div>
                  <div className="flex justify-between" style={{ color: "var(--muted)" }}><span>Deposit ({Math.round(selected.deposit_percent)}%)</span><span style={{ color: "var(--ink)" }}>{money(selected.deposit_amount)} {selected.deposit_paid ? "✓" : ""}</span></div>
                  <div className="flex justify-between" style={{ color: "var(--muted)" }}><span>Balance</span><span style={{ color: "var(--ink)" }}>{money(selected.balance_due)} {selected.balance_paid ? "✓" : ""}</span></div>
                </div>

                {/* Set quote price for custom "Etc." items */}
                {selected.is_quote && !selected.is_large_order && (
                  <div className="rounded-xl p-3" style={{ background: "var(--accent-soft)" }} data-testid="quote-pricing">
                    <div className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: "var(--ink)" }}>
                      Set custom quote — call {selected.customer_phone}
                    </div>
                    {selected.items.map((it, i) =>
                      it.is_quote ? (
                        <div key={i} className="flex items-center gap-2 mb-2">
                          <span className="flex-1 text-sm truncate" style={{ color: "var(--ink)" }}>
                            {it.quantity}× {it.product_name}
                            {it.message_on_cake ? ` — "${it.message_on_cake}"` : ""}
                          </span>
                          <input
                            type="number"
                            value={quoteInputs[i] ?? ""}
                            onChange={(e) => setQuoteInputs((q) => ({ ...q, [i]: e.target.value }))}
                            data-testid={`quote-price-${i}`}
                            placeholder="$ / unit"
                            className="w-24 rounded-lg px-2 py-2 outline-none text-sm"
                            style={{ background: "var(--surface)", border: "1px solid var(--border)" }}
                          />
                        </div>
                      ) : null
                    )}
                    <button onClick={() => sendQuote(selected)} data-testid="send-quote-btn" className="btn-brand w-full rounded-xl py-2.5 text-sm font-semibold mt-1">
                      Send quote to customer
                    </button>
                  </div>
                )}

                {/* Set one total for a large / bulk order */}
                {selected.is_quote && selected.is_large_order && (
                  <div className="rounded-xl p-3" style={{ background: "var(--accent-soft)" }} data-testid="large-order-pricing">
                    <div className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: "var(--ink)" }}>
                      Large order — set one total · call {selected.customer_phone}
                    </div>
                    {selected.notes && (
                      <p className="text-xs mb-2" style={{ color: "var(--muted)" }}>&ldquo;{selected.notes}&rdquo;</p>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-semibold" style={{ color: "var(--ink)" }}>$</span>
                      <input
                        type="number"
                        value={totalInput}
                        onChange={(e) => setTotalInput(e.target.value)}
                        data-testid="large-order-total-input"
                        placeholder="Total price"
                        className="flex-1 rounded-lg px-3 py-2 outline-none text-sm"
                        style={{ background: "var(--surface)", border: "1px solid var(--border)" }}
                      />
                    </div>
                    <button onClick={() => sendOrderTotal(selected)} data-testid="send-order-total-btn" className="btn-brand w-full rounded-xl py-2.5 text-sm font-semibold mt-2">
                      Send total to customer
                    </button>
                  </div>
                )}

                {/* Deposit override */}
                {!selected.deposit_paid && (
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide mb-2 flex items-center gap-1.5" style={{ color: "var(--muted)" }}><Percent size={13} /> Adjust deposit %</div>
                    <div className="flex gap-2">
                      <input value={depInput} onChange={(e) => setDepInput(e.target.value)} data-testid="deposit-input" type="number" placeholder={`${Math.round(selected.deposit_percent)}`} className="flex-1 rounded-xl px-3 py-2 outline-none" style={{ background: "var(--surface)", border: "1px solid var(--border)" }} />
                      <button onClick={() => applyDeposit(selected)} data-testid="apply-deposit-btn" className="btn-brand px-4 rounded-xl text-sm font-semibold">Apply</button>
                    </div>
                  </div>
                )}

                {/* Status actions */}
                <div className="space-y-2">
                  {selected.status === "pending" && (
                    <div className="grid grid-cols-2 gap-2">
                      <button onClick={() => setStatus(selected, "accepted")} data-testid="accept-order-btn" className="btn-brand rounded-xl py-2.5 font-semibold inline-flex items-center justify-center gap-1.5"><Check size={16} /> Accept</button>
                      <button onClick={() => setStatus(selected, "rejected")} data-testid="reject-order-btn" className="rounded-xl py-2.5 font-semibold inline-flex items-center justify-center gap-1.5 text-red-600" style={{ border: "1px solid #fca5a5" }}><X size={16} /> Reject</button>
                    </div>
                  )}
                  {NEXT[selected.status] && (
                    <button onClick={() => setStatus(selected, NEXT[selected.status])} data-testid="advance-status-btn" className="btn-brand w-full rounded-xl py-2.5 font-semibold inline-flex items-center justify-center gap-1.5">
                      Advance to {NEXT[selected.status]} <ChevronRight size={16} />
                    </button>
                  )}
                  {selected.deposit_paid && !selected.balance_paid && (
                    <button onClick={() => markPaid(selected)} data-testid="mark-balance-paid-btn" className="w-full rounded-xl py-2.5 font-semibold inline-flex items-center justify-center gap-1.5" style={{ border: "1px solid var(--border)", color: "var(--ink)" }}>
                      <CreditCard size={16} /> Mark balance paid (cash)
                    </button>
                  )}
                  <button
                    onClick={() => setConfirm({ type: "delete", order: selected })}
                    data-testid="delete-order-btn"
                    className="w-full rounded-xl py-2.5 font-semibold inline-flex items-center justify-center gap-1.5 text-red-600"
                    style={{ border: "1px solid #fca5a5" }}
                  >
                    <Trash2 size={16} /> Delete order
                  </button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && !working && setConfirm(null)}>
        <AlertDialogContent data-testid="order-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.type === "clear" ? "Clear all orders?" : "Delete this order?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.type === "clear"
                ? `This permanently deletes every order for this business. This cannot be undone.`
                : `Order ${confirm?.order?.order_number} will be permanently deleted. This cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={working}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); runConfirm(); }}
              data-testid="order-confirm-delete-btn"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {working ? "Deleting…" : confirm?.type === "clear" ? "Clear all" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
